/**
 * API endpoints CAREX.
 *
 * Chaque module expose les opérations CRUD pour une entité.
 */
import api from "./client";

// ─── Clients ────────────────────────────────
export const clientsApi = {
  list: (params) => api.get("/clients", params),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post("/clients", data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// ─── Recherche entreprises (API gouv) ──────
export const entreprisesApi = {
  search: (q) => api.get("/geo/entreprises/search", { q }),
};

// ─── Dossiers ───────────────────────────────
export const dossiersApi = {
  list: (params) => api.get("/dossiers", params),
  get: (id) => api.get(`/dossiers/${id}`),
  getComplet: (id) => api.get(`/dossiers/${id}/complet`),
  create: (data) => api.post("/dossiers", data),
  update: (id, data) => api.put(`/dossiers/${id}`, data),
  delete: (id) => api.delete(`/dossiers/${id}`),
  changeStatut: (id, statut) => api.patch(`/dossiers/${id}/statut`, { statut }),
  getTransitions: (id) => api.get(`/dossiers/${id}/transitions`),
  getPhaseProgress: (id) => api.get(`/dossiers/${id}/phase-progress`),
  validatePhase: (id, phase) => api.post(`/dossiers/${id}/phases/${phase}/validate`),
  getActivities: (id) => api.get(`/dossiers/${id}/activities`),
  changeExpert: (id, expertId) =>
    api.patch(`/dossiers/${id}/expert`, { expert_id: expertId }),
};

// ─── Expertises ─────────────────────────────
export const expertisesApi = {
  list: (params) => api.get("/expertises", params),
  get: (id) => api.get(`/expertises/${id}`),
  getComplete: (id) => api.get(`/expertises/${id}/complete`),
  create: (data) => api.post("/expertises", data),
  update: (id, data) => api.put(`/expertises/${id}`, data),
  delete: (id) => api.delete(`/expertises/${id}`),
};

// ─── Parcelles ──────────────────────────────
export const parcellesApi = {
  list: (params) => api.get("/parcelles", params),
  get: (id) => api.get(`/parcelles/${id}`),
  create: (data) => api.post("/parcelles", data),
  update: (id, data) => api.put(`/parcelles/${id}`, data),
  delete: (id) => api.delete(`/parcelles/${id}`),
};

// ─── Biens ──────────────────────────────────
export const biensApi = {
  list: (params) => api.get("/biens", params),
  get: (id) => api.get(`/biens/${id}`),
  create: (data) => api.post("/biens", data),
  update: (id, data) => api.put(`/biens/${id}`, data),
  delete: (id) => api.delete(`/biens/${id}`),
};

// ─── Études de marché ───────────────────────
export const etudesMarcheApi = {
  list: (params) => api.get("/etudes-marche", params),
  get: (id) => api.get(`/etudes-marche/${id}`),
  create: (data) => api.post("/etudes-marche", data),
  update: (id, data) => api.put(`/etudes-marche/${id}`, data),
  delete: (id) => api.delete(`/etudes-marche/${id}`),
};

// ─── Transactions comparables ───────────────
export const transactionsApi = {
  list: (params) => api.get("/etudes-marche/transactions/list", params),
  get: (id) => api.get(`/etudes-marche/transactions/${id}`),
  create: (data) => api.post("/etudes-marche/transactions", data),
  update: (id, data) => api.put(`/etudes-marche/transactions/${id}`, data),
  delete: (id) => api.delete(`/etudes-marche/transactions/${id}`),
  toggleExclue: (id) => api.patch(`/etudes-marche/transactions/${id}/toggle-exclue`),
};

// ─── Valorisations ──────────────────────────
export const valorisationsApi = {
  list: (params) => api.get("/valorisations", params),
  get: (id) => api.get(`/valorisations/${id}`),
  create: (data) => api.post("/valorisations", data),
  update: (id, data) => api.put(`/valorisations/${id}`, data),
  delete: (id) => api.delete(`/valorisations/${id}`),
};

// ─── Ajustements ────────────────────────────
export const ajustementsApi = {
  list: (params) => api.get("/valorisations/ajustements/list", params),
  get: (id) => api.get(`/valorisations/ajustements/${id}`),
  create: (data) => api.post("/valorisations/ajustements", data),
  update: (id, data) => api.put(`/valorisations/ajustements/${id}`, data),
  delete: (id) => api.delete(`/valorisations/ajustements/${id}`),
};

// ─── Photos ────────────────────────────────
export const photosApi = {
  list: (expertiseId) => api.get(`/photos/expertises/${expertiseId}/photos`),
  upload: (expertiseId, file, categorie = "exterieur", legende = "") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("categorie", categorie);
    formData.append("legende", legende);
    return api.upload(`/photos/expertises/${expertiseId}/photos`, formData);
  },
  update: (photoId, data) => api.patch(`/photos/photos/${photoId}`, data),
  reorder: (expertiseId, photos) =>
    api.patch(`/photos/expertises/${expertiseId}/photos/reorder`, { photos }),
  delete: (photoId) => api.delete(`/photos/photos/${photoId}`),
};

// ─── Documents ─────────────────────────────
export const documentsApi = {
  list: (expertiseId) => api.get(`/documents/expertises/${expertiseId}/documents`),
  create: (expertiseId, label) =>
    api.post(`/documents/expertises/${expertiseId}/documents`, { label }),
  remove: (docId) => api.delete(`/documents/${docId}`),
  updateStatus: (docId, statut) => api.patch(`/documents/${docId}`, { statut }),
  batchUpdate: (expertiseId, updates) =>
    api.patch(`/documents/expertises/${expertiseId}/documents/batch`, { updates }),
  upload: (expertiseId, typeDocument, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.upload(
      `/documents/expertises/${expertiseId}/documents/${typeDocument}/upload`,
      formData
    );
  },
  deleteFile: (docId) => api.delete(`/documents/${docId}/file`),
  analyze: (docId) => api.post(`/documents/${docId}/analyze`),
  getAnalysis: (docId) => api.get(`/documents/${docId}/analysis`),
  validate: (docId, acceptedFields) =>
    api.post(`/documents/${docId}/validate`, { accepted_fields: acceptedFields }),
  getFileUrl: (docId) => api.get(`/documents/${docId}/file-url`),
};

// ─── Utilisateurs (admin) ──────────────────
export const usersApi = {
  list: (params) => api.get("/users", params),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post("/users", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  listAssignable: () => api.get("/users/assignable"),
};

// ─── Admin Dossiers ───────────────────────────────
export const adminDossiersApi = {
  list: (params) => api.get("/admin/dossiers", params),
  delete: (id) => api.delete(`/admin/dossiers/${id}`),
};

// ─── Profil ────────────────────────────────
export const profileApi = {
  updateProfile: (data) => api.put("/auth/profile", data),
  changePassword: (data) => api.put("/auth/password", data),
};

// ─── Audit (admin) ─────────────────────────
export const auditApi = {
  list: (params) => api.get("/audit", params),
};

// ─── Rapports ───────────────────────────────
export const rapportsApi = {
  /** Génère une nouvelle version du rapport. Retourne les métadonnées JSON. */
  generer: (dossierId, format = 'docx', commentaire = null) =>
    api.post("/rapports/generer", { dossier_id: dossierId, format, commentaire }),
  /** Métadonnées du rapport d'un dossier (avec version courante). */
  getRapport: (dossierId) => api.get(`/rapports/dossiers/${dossierId}`),
  /** Liste toutes les versions du rapport d'un dossier. */
  listVersions: (dossierId) => api.get(`/rapports/dossiers/${dossierId}/versions`),
  /** Télécharge le fichier d'une version spécifique. */
  telecharger: (versionId) =>
    api.downloadGet(`/rapports/versions/${versionId}/telecharger`),
  /** Transition de statut (brouillon→validé, validé→remis_au_client). */
  changerStatut: (versionId, statut) =>
    api.patch(`/rapports/versions/${versionId}/statut`, { statut }),
  /** Supprime une version brouillon. */
  supprimer: (versionId) => api.delete(`/rapports/versions/${versionId}`),
};
